/**
 * Gift / bonus line-item reconciliation (mystery gift, gift box, extra keyring,
 * orphan personalization cleanup, clear all-free carts).
 *
 * Ported out of sections/cart-drawer-tw.liquid (cart-slideout-to-summary-popup-plan.md
 * Phase 3) so gift logic survives after the jQuery cart drawer is removed. Listens on
 * `cart:updated` the same way the drawer script did; mutates cart via
 * window.batchUpdateCart (tw_js.liquid) when needed.
 */
(function () {
  'use strict';

  let isRunning = false;
  // A cart:updated or checkout request that came in while a pass was running (that pass decided on
  // the cart from before it), or a pass that just changed the cart and needs its result checked:
  // run again once the pass is done. Dropping these used to leave e.g. a second travel kit line,
  // or a checkout redirect decided on a cart that had changed since.
  let rerunRequested = false;
  // Back-to-back passes, capped so a gift line that can never be added (e.g. sold out) can't loop.
  let chainedRuns = 0;
  const MAX_CHAINED_RUNS = 3;

  function finishRun() {
    isRunning = false;
    if (!rerunRequested) {
      chainedRuns = 0;
      return;
    }
    rerunRequested = false;
    if (chainedRuns >= MAX_CHAINED_RUNS) {
      console.warn('[cart-gift-reconciliation] Gift lines still changing after', MAX_CHAINED_RUNS, 'passes - stopping');
      chainedRuns = 0;
      redirectIfCheckoutRequested();
      return;
    }
    chainedRuns += 1;
    // Reruns stay silent: the pass the customer triggered already showed its loading state.
    reconcile(true);
  }

  // Every cart call of the checkout is done: take the loading down, then leave for checkout.
  function goToCheckout() {
    window._redirectToCheckout = false;
    clearLoadingForCache();
    // Summary popups free their checkout button (shared/cart-summary/useCheckoutLock.js).
    document.dispatchEvent(new CustomEvent('checkout:done'));
    window.location.href = '/checkout';
  }
  window.goToCheckout = goToCheckout;

  // Whole-page checkout loading (body.checkout-loading, tw_css.liquid): from the checkout click
  // until its cart calls are done (goToCheckout) - over the summary panel too, so nothing can be
  // clicked meanwhile. A checkout that stops instead (an error toast - every failed add/validation shows
  // one -, a bfcache restore, or nothing happening for a while) takes it down again.
  const CHECKOUT_LOADING_CLASS = 'checkout-loading';
  // Long enough for a slow photo-charm upload (bundle-builder-v2.jsx waits up to 15s + 30s retries).
  const CHECKOUT_LOADING_MAX_MS = 60000;
  let checkoutLoadingTimer = null;

  function showCheckoutLoading() {
    document.body.classList.add(CHECKOUT_LOADING_CLASS);
    clearTimeout(checkoutLoadingTimer);
    checkoutLoadingTimer = setTimeout(hideCheckoutLoading, CHECKOUT_LOADING_MAX_MS);
  }

  function hideCheckoutLoading() {
    clearTimeout(checkoutLoadingTimer);
    document.body.classList.remove(CHECKOUT_LOADING_CLASS);
  }

  // A checkout that failed: every loading state it put up comes down (the page loading, the
  // builders' `loading_animation` + summary overlay - an open summary panel keeps the overlay as
  // its backdrop), the redirect flag is cleared, and `checkout:stopped` tells the builders to drop
  // their own redirect flag - otherwise the next plain Add to cart would jump to checkout.
  function stopCheckout() {
    hideCheckoutLoading();
    window._redirectToCheckout = false;
    document.body.classList.remove('loading_animation');
    const overlay = document.querySelector('.js-summary-overlay');
    if (overlay && !document.querySelector('.js-summary-panel.open')) overlay.style.display = 'none';
    document.dispatchEvent(new CustomEvent('checkout:stopped'));
  }

  window.showCheckoutLoading = showCheckoutLoading;
  window.hideCheckoutLoading = hideCheckoutLoading;
  window.stopCheckout = stopCheckout;

  // Every failed checkout step shows an error toast (shared/notify.js notifyError) - while a
  // checkout is in flight, that toast is what stops it.
  document.addEventListener('exchange-life:notify', function (event) {
    if ((event.detail && event.detail.type) !== 'error') return;
    if (document.body.classList.contains(CHECKOUT_LOADING_CLASS) || window._redirectToCheckout) stopCheckout();
  });

  function clearLoadingForCache() {
    hideCheckoutLoading();
    document.body.classList.remove('loading_animation');
    const overlay = document.querySelector('.js-summary-overlay');
    if (overlay && !document.querySelector('.js-summary-panel.open')) overlay.style.display = 'none';
  }

  // Safety net for Back from checkout (bfcache): no loading left in the cached page - pagehide
  // changes are what the bfcache keeps, pageshow clears it again in case a browser kept an earlier
  // snapshot.
  window.addEventListener('pagehide', clearLoadingForCache);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) clearLoadingForCache();
  });

  // Bail-outs below that can't reconcile gifts must still honor a pending checkout request
  // (reconcileGiftsAndCheckout / the builders set the flag and rely on this file to redirect) -
  // otherwise Checkout does nothing and the flag lingers until some unrelated cart:updated.
  function redirectIfCheckoutRequested() {
    if (!window._redirectToCheckout) return;
    goToCheckout();
  }

  function cartUpdates(event) {
    // TEMP DEBUG (checkout-redirect troubleshooting - safe to remove once resolved).
    console.log('[cart-gift-reconciliation][debug] cartUpdates called, isRunning =', isRunning, 'window._redirectToCheckout =', window._redirectToCheckout);
    // Background removals from the summary popups (shared/cart-summary/cartMutationQueue.js)
    // skip reconciliation: gift/bonus lines are hidden there and reconciled once at checkout
    // (reconcileGiftsAndCheckout below), so removing items never makes gift lines jump around.
    // The gifts a removal disqualifies already left in that same request (giftLineReductions).
    if (event && event.detail && event.detail.__deferGiftReconcile && !window._redirectToCheckout) {
      return;
    }
    if (isRunning) {
      console.log('[cart-gift-reconciliation][debug] already running - queued a rerun');
      rerunRequested = true;
      return;
    }
    chainedRuns = 0;
    // Set by hydrate-time background cart cleanup (bundle-builder-v2.jsx / charm-holder-
    // builder.jsx) right after a customer comes straight back from checkout to the same PDP -
    // that cleanup is meant to be invisible, so any gift/bonus reconciliation it triggers here
    // must stay invisible too, instead of popping the same loading_animation overlay a real
    // user-initiated add-to-cart/checkout would show.
    reconcile(Boolean(event && event.detail && event.detail.__silentReconcile));
  }

  function notifyCheckoutFailed() {
    const common = (window.translations && window.translations.bundle_builder && window.translations.bundle_builder.common) || {};
    document.dispatchEvent(new CustomEvent('exchange-life:notify', {
      detail: { message: common.add_failed || 'Could not add your selections to the cart. Please try again.', type: 'error' },
    }));
  }

  // Decides on this tab's window.cartJSON (every cart:updated carries the server's cart). A cart
  // changed outside this tab (another tab, Back from checkout) can be stale here - the
  // cart-zero-price-block validation function (function-exchange-life) blocks the gift-only and
  // travel-kit-without-5-charms carts at checkout, so no extra /cart.js read is needed here.
  function reconcile(silent) {
    try {
      const cartData = window.cartJSON || window.cart;
      if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
        console.warn('[cart-gift-reconciliation] Invalid cart data:', cartData);
        redirectIfCheckoutRequested();
        return;
      }

      // Subscription lines (e.g. the travel community membership, sold with a selling plan)
      // never count toward gifts - buying only the membership must not add a mystery gift etc.
      const isSubscriptionLine = (item) => !!(item && item.selling_plan_allocation);

      // Subtotal 0 but still has lines → only free items → clear cart. Skipped when a
      // subscription is in the cart: a free-trial plan can legitimately make the total 0.
      if (
        cartData.items.length > 0 &&
        (cartData.total_price ?? 0) === 0 &&
        !cartData.items.some(isSubscriptionLine)
      ) {
        const clearUrl =
          (window.routes && window.routes.cart && window.routes.cart.clear) || '/cart/clear';
        isRunning = true;
        fetch(`${clearUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })
          .then(() => {
            if (typeof window.update_cart_content === 'function') {
              return window.update_cart_content('silent');
            }
            return null;
          })
          .catch((err) => console.error('[cart-gift-reconciliation] clearCart failed:', err))
          .finally(finishRun);
        return;
      }

      const addItems = [];
      const updateItems = [];

      const config = {
        autoAddMysteryGift: window.autoAddMysteryGift,
        mysteryShippingPrice: window.mysteryShippingPrice || 0,
        mysteryVariantId: window.mysteryVariantId || null,
        giftBoxId: window.giftBoxId || 49705259893079,
        extraKeyringId: window.extraKeyringId || 51926914662743,
        keychainProductId: window.keychainProductId || 9604548985175,
        shippingInsuranceVariantId: window.shippingInsuranceVariantId || 48981080375639,
        personalizationVariantId: window.personalizationVariantId || 49192406286679,
      };

      const isExcludedFromGiftEligibility = (item) => {
        if (!item) return true;
        if (isSubscriptionLine(item)) return true;
        const itemVariantId = item.variant_id || (item.variant && item.variant.id);
        const itemProductId = item.product_id || (item.product && item.product.id);
        const insuranceId = config.shippingInsuranceVariantId;

        if (itemVariantId === config.mysteryVariantId) return true;
        if (itemVariantId === config.giftBoxId) return true;
        if (itemVariantId === config.extraKeyringId) return true;
        if (itemVariantId === config.personalizationVariantId) return true;
        if (itemVariantId === insuranceId || itemProductId === insuranceId) return true;
        if (item.properties && item.properties.__add_ons) return true;
        if (item.properties && item.properties.__mystery_gift) return true;
        return false;
      };

      // Only the mystery gift needs this id - returning here used to skip the travel kit and
      // keyring checks too, so a travel kit stayed in a cart with no charms left.
      if (!config.mysteryVariantId) {
        console.warn('[cart-gift-reconciliation] Mystery variant ID not available; skipping mystery gift only');
      }

      // Every line of each gift variant - the same variant with different properties is a
      // separate line, and tracking only the last one used to leave the others in the cart.
      const mysteryLines = [];
      const giftBoxLines = [];
      const extraKeyringLines = [];
      let charmsCount = 0;
      let keychainQuantity = 0;
      let otherCharmHolderCount = 0;
      const personalizationKeys = [];
      // Personalization lines carrying a __group, and how many engraved units each __group's
      // owner line(s) actually hold - used below to drop orphans / fix quantity drift.
      const groupedPersonalizationItems = [];
      const engravedQtyByGroup = new Map();
      const hasEngravingLines = (props) =>
        Object.keys(props).some(
          (key) => /^Engraving Line \d+$/.test(key) && String(props[key] || '').trim() !== ''
        );

      cartData.items.forEach((item) => {
        if (!item) return;

        const itemVariantId = item.variant_id || (item.variant && item.variant.id);
        const itemProductId = item.product_id || (item.product && item.product.id);
        const props = item.properties || {};

        if (itemVariantId === config.personalizationVariantId) {
          personalizationKeys.push(item.key);
          if (props.__group) groupedPersonalizationItems.push(item);
        } else if (props.__group && hasEngravingLines(props)) {
          const group = String(props.__group);
          engravedQtyByGroup.set(group, (engravedQtyByGroup.get(group) || 0) + (item.quantity || 0));
        }

        if (itemVariantId === config.mysteryVariantId) mysteryLines.push(item);
        if (item.properties && item.properties.__type === 'charm_set') {
          charmsCount += item.quantity || 0;
        }

        if (itemVariantId === config.giftBoxId) giftBoxLines.push(item);

        if (itemVariantId === config.extraKeyringId) extraKeyringLines.push(item);

        if (itemProductId === config.keychainProductId) {
          keychainQuantity += item.quantity || 0;
        }

        if (
          item.properties &&
          item.properties.__type === 'charm_holder' &&
          itemProductId !== config.keychainProductId
        ) {
          otherCharmHolderCount += item.quantity || 0;
        }
      });

      if (charmsCount === 0 && personalizationKeys.length > 0) {
        personalizationKeys.forEach((key) => {
          updateItems.push({ id: key, quantity: 0 });
        });
      } else {
        // Each Personalization line must match the engraved line sharing its __group: remove it
        // when that line is gone (e.g. charm removed from the cart summary popup), and clamp its
        // quantity to the engraved quantity otherwise. Lines without __group are left alone.
        const allocatedByGroup = new Map();
        groupedPersonalizationItems.forEach((item) => {
          const group = String(item.properties.__group);
          const remaining = Math.max(
            0,
            (engravedQtyByGroup.get(group) || 0) - (allocatedByGroup.get(group) || 0)
          );
          const expectedQty = Math.min(item.quantity || 0, remaining);
          allocatedByGroup.set(group, (allocatedByGroup.get(group) || 0) + expectedQty);
          if (expectedQty !== item.quantity) {
            updateItems.push({ id: item.key, quantity: expectedQty });
          }
        });
      }

      const realProductsCount = cartData.items.filter(
        (item) => !isExcludedFromGiftEligibility(item)
      ).length;

      // Brings a gift's lines to `quantity` in total: adds a line when there is none, otherwise
      // the first line takes the quantity and every other line is removed.
      const setGiftQuantity = (lines, variantId, quantity) => {
        if (quantity > 0 && lines.length === 0) {
          addItems.push({ id: variantId, quantity: quantity, properties: { __mystery_gift: 'true' } });
          return;
        }
        lines.forEach((line, index) => {
          const expected = index === 0 ? quantity : 0;
          if ((line.quantity || 0) !== expected) updateItems.push({ id: line.key, quantity: expected });
        });
      };

      if (!config.mysteryVariantId) {
        // No mystery gift to manage.
      } else if (config.autoAddMysteryGift) {
        setGiftQuantity(mysteryLines, config.mysteryVariantId, realProductsCount > 0 ? 1 : 0);
      } else if (mysteryLines.length > 1 || (mysteryLines[0] && mysteryLines[0].quantity > 1)) {
        // Not auto-added, but never more than one.
        setGiftQuantity(mysteryLines, config.mysteryVariantId, 1);
      }

      setGiftQuantity(giftBoxLines, config.giftBoxId, charmsCount >= 5 ? 1 : 0);

      let requiredKeyrings = 0;
      if (keychainQuantity > 0 && charmsCount >= 20) {
        const charmsAfterKeychain = Math.max(0, charmsCount - keychainQuantity * 20);
        const charmKeyrings = Math.floor(charmsAfterKeychain / 20);
        requiredKeyrings = Math.max(
          0,
          keychainQuantity + charmKeyrings - otherCharmHolderCount
        );
      }
      setGiftQuantity(extraKeyringLines, config.extraKeyringId, requiredKeyrings);

      // TEMP DEBUG (checkout-redirect troubleshooting - safe to remove once resolved).
      console.log('[cart-gift-reconciliation][debug] decision point - addItems =', addItems, 'updateItems =', updateItems, 'window._redirectToCheckout =', window._redirectToCheckout, 'silent =', silent);

      if (addItems.length > 0 || updateItems.length > 0) {
        if (typeof window.batchUpdateCart === 'function') {
          console.log('[cart-gift-reconciliation][debug] calling batchUpdateCart - awaiting its actual completion before clearing isRunning (batchUpdateCart itself checks window._redirectToCheckout again once its own cascade settles, so the redirect can happen from either place)');
          isRunning = true;
          // batchUpdateCart now RETURNS its full promise chain (add/update fetches +
          // update_cart_content) - await that instead of a blind setTimeout(0) before clearing
          // isRunning. The old fixed-delay guess raced against real network latency: on Safari,
          // the fetch cascade could settle AFTER the timeout fired (leaving isRunning stuck true
          // and silently dropping the next legitimate cart:updated pass) or BEFORE it (leaving a
          // stale isRunning=true window that dropped the very cart:updated dispatched by this
          // cascade's own update_cart_content call) - either way, this bundle-builder-v2/
          // charm-holder-builder checkout flow's window._redirectToCheckout could end up never
          // acted on, stranding the customer on a loading overlay with no redirect. Awaiting the
          // real promise removes the race entirely.
          // skipRedirect: the pass below re-reads the real cart and redirects once nothing is
          // left to change, instead of leaving for checkout on what this pass assumed.
          Promise.resolve(window.batchUpdateCart(addItems, updateItems, silent, true))
            .catch((err) => console.error('[cart-gift-reconciliation] batchUpdateCart failed:', err))
            .finally(() => {
              console.log('[cart-gift-reconciliation][debug] batchUpdateCart cascade settled, isRunning cleared');
              rerunRequested = true;
              finishRun();
            });
        } else {
          console.warn('[cart-gift-reconciliation] batchUpdateCart unavailable');
        }
      } else if (window._redirectToCheckout && cartData.items.length === 0) {
        // Nothing earned its gifts (e.g. the charms were removed in another tab) - the cart is
        // empty now, so there is nothing to check out.
        stopCheckout();
      } else if (window._redirectToCheckout) {
        console.log('[cart-gift-reconciliation][debug] no gift/bonus changes needed - redirecting to /checkout now');
        goToCheckout();
      } else {
        console.log('[cart-gift-reconciliation][debug] no gift/bonus changes needed AND _redirectToCheckout is falsy - nothing to do');
      }
    } catch (error) {
      console.error('[cart-gift-reconciliation] Error in cartUpdates:', error);
      isRunning = false;
      // A checkout waiting on this pass would otherwise hang: say so and stop it (the error toast
      // stops it - see the exchange-life:notify listener above).
      if (window._redirectToCheckout) notifyCheckoutFailed();
    }
  }

  window.cartUpdates = cartUpdates;

  // Checkout from a summary popup with nothing new to add: reconcile gift/bonus lines against the
  // real cart, then redirect - from reconcile() once a pass finds nothing left to change (a
  // request arriving mid-pass is picked up by the rerun in finishRun).
  window.reconcileGiftsAndCheckout = function () {
    window._redirectToCheckout = true;
    cartUpdates();
  };

  function boot() {
    cartUpdates();
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }

  document.addEventListener('cart:updated', function (event) {
    cartUpdates(event);
  });
})();
